import { Stack } from '@mui/material'
import { C1Button } from 'c1ui'
import { C1Icon } from 'components/core/icons/C1Icon'
import RequestAccessFormV2Dialog from 'components/core/simple-entitlement-request-dialog/RequestAccessFormV2Dialog'
import { TaskViewLinkV2 } from 'components/core/task-list-v2/task-list-v2-components/TaskViewLinkV2'
import { RevokeGrantRequestDialog } from 'components/pages/entitlement/users/list/revokeGrantRequestDialog'
import { AppEntitlementWithUserBindingValue } from 'lib/actions/apps/app-entitlement'
import { useSimpleModal } from 'lib/modals'
import { CircleCheck } from 'lucide-react'
import { Task } from 'pbts/c1/api/task/v1/task'
import { User, UserRef } from 'pbts/c1/api/user/v1/user'
import { FC } from 'react'
import { FormattedMessage } from 'react-intl'
import { RequestCatalogUserEntitlementPerAppValue } from 'redux_toolkit/browse-access'
import { getUserMultiApi } from 'redux_toolkit/users'

type AccessRequestActionsProps = {
    requestCatalogEntitlement: RequestCatalogUserEntitlementPerAppValue
    task?: Task
    user: User
    showAccess?: boolean
}

export const AccessRequestActions: FC<AccessRequestActionsProps> = ({
    requestCatalogEntitlement,
    task,
    user,
    showAccess,
}) => {
    const { open } = useSimpleModal()
    const appEntitlementUserBinding = requestCatalogEntitlement?.appEntitlementUserBinding
    const taskToDisplay = requestCatalogEntitlement?.task || task

    const openRequestDialog = (e: React.MouseEvent): void => {
        e.stopPropagation()
        if (requestCatalogEntitlement.entitlement) {
            open(RequestAccessFormV2Dialog, {
                entitlements: [requestCatalogEntitlement.entitlement],
                user,
            })()
        }
    }

    if (taskToDisplay) {
        return <TaskViewLinkV2 task={taskToDisplay} />
    }

    if (!appEntitlementUserBinding) {
        return <C1Button color="primary" variant="outlined" onClick={openRequestDialog}>
            <FormattedMessage id="access_request_request_access" defaultMessage="Request" />
        </C1Button>
    }

    if (appEntitlementUserBinding && appEntitlementUserBinding.appEntitlementUserBindingDeprovisionAt) {
        return <Stack spacing={1} direction="row" justifyContent="flex-end">
            <C1Button color="primary" variant="outlined" onClick={openRequestDialog}>
                <FormattedMessage id="access_request_request_extension" defaultMessage="Extend" />
            </C1Button>
            <C1Button
                color="error"
                variant="outlined"
                onClick={(e): void => {
                    e.stopPropagation()
                    open(RevokeGrantRequestDialog, {
                        appUserId: appEntitlementUserBinding.appUser?.appUser?.id,
                        appEntitlement: requestCatalogEntitlement.entitlement,
                        resource: requestCatalogEntitlement.entitlement?.appResource,
                    })()
                }}
            >
                <FormattedMessage defaultMessage="Remove" id="access_request_revoke_access" />
            </C1Button>
        </Stack>
    }

    if (showAccess && appEntitlementUserBinding) {
        return <C1Icon icon={CircleCheck} size="sm" sx={{ color: 'success.light' }} />
    }
    return null
}

export const AccessRequestActionsWithUserFetch = ({
    row,
}: {
    row: AppEntitlementWithUserBindingValue
}): React.ReactElement | null => {
    const identityUserId = row.appEntitlementUserBinding?.appUser?.appUser?.identityUserId || ''
    const { data: user } = getUserMultiApi.useMultiGet(
        UserRef.create({ id: identityUserId }),
        { actionable: !!identityUserId },
    )
    if (!user) return null
    return <AccessRequestActions requestCatalogEntitlement={row} user={user} />
}
